import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IconLayoutKanban } from '@tabler/icons-react';
import AppShell from '../components/AppShell';
import { alertDialog, confirmDialog } from '../components/AppDialog';
import { PageHeader, Button } from '../components/ui';
import { supportBoardAPI } from '../api/client';
import useIsMobile from '../hooks/useIsMobile';
import { COLORS, RADIUS, TONES, badge, btn, card, input as inputStyle } from '../utils/uiStyles';
import AssigneeTags, { assigneeIdsOf, assigneeNames } from './supportboard/AssigneeTags';
import CalendarGrid from './supportboard/CalendarGrid';
import ProjectModal from './supportboard/ProjectModal';
import ScheduleModal from './supportboard/ScheduleModal';
import { buildProjectColorMap, projectColorOf } from './supportboard/projectColors';

export const STATUS_LABELS = {
  planned: '계획중',
  in_progress: '진행중',
  done: '완료',
  on_hold: '보류',
};

export const SINGLE_SELECT_HINT = '일정을 추가하려면 프로젝트를 하나만 선택하세요';

// [프로젝트 목록 | 캘린더 | 일정 패널] 3단에 필요한 본문 폭 = 목록 240 + 캘린더 734 + 일정 패널 285 + 간격 16×2 = 1291.
// 캘린더 734 = CalendarGrid 안쪽 최소 폭 700 + 좌우 padding 16×2 + 테두리 1×2 — 이보다 좁으면 금·토 열이 잘린다.
// 🔴 판정은 뷰포트가 아니라 사이드바·main 여백을 뺀 실제 본문 폭(보드 상자)으로 한다 — 뷰포트 1309(사이드바 260)는
//    본문이 약 993 이라 3단이 안 들어가는데, 뷰포트 기준으로는 3단이 그려져 일정 패널이 화면 밖으로 넘치고 금·토 열이 잘렸다.
//    (사이드바 기본 폭이 238 로 바뀐 지금은 뷰포트 1309 → 본문 약 1015 — 여전히 1291 에 못 미쳐 판정 결과는 같다.)
const PROJECT_LIST_WIDTH = 240;
const CALENDAR_MIN_WIDTH = 734;
const SCHEDULE_PANEL_MIN_WIDTH = 285;
const BOARD_GAP = 16;
const THREE_COLUMN_MIN_WIDTH = PROJECT_LIST_WIDTH + CALENDAR_MIN_WIDTH + SCHEDULE_PANEL_MIN_WIDTH + BOARD_GAP * 2;
// stacked 아래 칸 [캘린더 | 일정 패널] 2단에 필요한 폭(1035). 그보다 좁으면 일정 패널을 캘린더 아래로 내린다.
const CALENDAR_PANEL_MIN_WIDTH = CALENDAR_MIN_WIDTH + SCHEDULE_PANEL_MIN_WIDTH + BOARD_GAP;
const THREE_COLUMN_TEMPLATE = `${PROJECT_LIST_WIDTH}px minmax(${CALENDAR_MIN_WIDTH}px, 2fr) minmax(${SCHEDULE_PANEL_MIN_WIDTH}px, .8fr)`;
const CALENDAR_PANEL_TEMPLATE = `minmax(${CALENDAR_MIN_WIDTH}px, 2fr) minmax(${SCHEDULE_PANEL_MIN_WIDTH}px, .8fr)`;
// 보드 상자 폭을 못 잴 때(jsdom 등)의 대비책. gwViewMode='mobile'·터치 기기도 이 판정으로 stacked 가 된다.
const BOARD_STACK_QUERY = '(max-width: 1279px)';
// 선택 그룹(약 40px) + 목록 640px ≈ 기본 캘린더 높이. 카드가 많아도 목록만 이 높이 안에서 스크롤된다.
const PROJECT_LIST_MAX_HEIGHT = 640;
const PROJECT_LIST_MAX_HEIGHT_STACKED = 240;
// 🔴 카드 목록 열은 최소 폭을 0 으로 묶는다 — 3단에서 열 정의가 없으면 암묵 auto 열이 nowrap 이름의 전체 폭
//    (min-content)까지 늘어나 카드가 목록 밖으로 넘쳤다(이름 오른쪽·상태 배지·%·바 끝이 잘리고 가로 스크롤바가 생겼다).
//    overflowX:hidden 만으로는 스크롤바만 숨겨지고 잘림은 그대로다. stacked 도 220 보다 좁은 칸(150% 확대)에서 넘치지 않게 min() 으로 묶는다.
const PROJECT_LIST_COLUMNS = 'minmax(0, 1fr)';
const PROJECT_LIST_COLUMNS_STACKED = 'repeat(auto-fill, minmax(min(220px, 100%), 1fr))';
// 터치에서는 title 툴팁이 안 뜬다 → 카드를 길게 누르면 2줄로 잘린 이름을 펼친다. 그보다 많이 움직이면 스크롤로 보고 취소한다.
const NAME_LONG_PRESS_MS = 500;
const NAME_LONG_PRESS_MOVE_PX = 10;

function apiErrorMessage(error) {
  return error.response?.data?.detail || error.message || '요청을 처리하지 못했습니다.';
}

function localDateKey(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function projectProgressWidth(progress) {
  const number = Number(progress);
  const normalized = Number.isFinite(number) ? Math.min(100, Math.max(0, number)) : 0;
  return Math.round(normalized) + '%';
}

// 보관함 = 완료된 프로젝트. 기본 화면은 이 상태를 빼고, '보관함' 뷰는 이 상태만 모은다.
export const ARCHIVED_STATUS = 'done';

export function isArchivedProject(project) {
  return project?.status === ARCHIVED_STATUS;
}

function statusTone(status) {
  if (status === 'done') return 'success';
  if (status === 'on_hold') return 'warn';
  return 'default';
}

function formatDate(value) {
  return value ? value.replaceAll('-', '.') : '-';
}

// 선택 id 는 항상 화면 목록 순서로 정렬 — 색 배정(목록 index)·조회 순서·병합 목록 기준을 한곳으로 맞춘다.
function orderSelection(ids, projects) {
  const wanted = new Set(ids);
  return projects.filter((project) => wanted.has(project.id)).map((project) => project.id);
}

function sameIdList(a, b) {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

// 프로젝트별 조회는 배치 API 가 없어 N건을 나눠 보낸다. 상한이 없으면 '전체 선택' 한 번에
// 프로젝트 수만큼 GET 이 한꺼번에 나가 nginx 레이트리밋(apizone)에 걸린다 → 동시 4건으로 제한.
const SCHEDULE_FETCH_LIMIT = 4;

async function mapWithLimit(items, limit, run) {
  const results = new Array(items.length);
  let cursor = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (cursor < items.length) {
      const index = cursor;
      cursor += 1;
      results[index] = await run(items[index]);
    }
  });
  await Promise.all(workers);
  return results;
}

export default function SupportBoardPage() {
  const today = localDateKey(new Date());
  const [projects, setProjects] = useState([]);
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);
  const [archiveView, setArchiveView] = useState(false);
  const [schedulesByProject, setSchedulesByProject] = useState({});
  const [assignees, setAssignees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [schedulesLoading, setSchedulesLoading] = useState(false);
  const [pageError, setPageError] = useState('');
  const [projectModal, setProjectModal] = useState({ open: false, project: null });
  const [scheduleModal, setScheduleModal] = useState({ open: false, schedule: null });
  const [viewDate, setViewDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1, 12);
  });
  const [scheduleTitle, setScheduleTitle] = useState('');
  const [scheduleStart, setScheduleStart] = useState(today);
  const [scheduleEnd, setScheduleEnd] = useState(today);
  const [scheduleAssigneeIds, setScheduleAssigneeIds] = useState([]);
  const [savingSchedule, setSavingSchedule] = useState(false);
  // gwViewMode='mobile' 이면 폭과 무관하게 stacked. 'desktop' 이어도 본문 폭이 모자라면 stacked 다(넘침 방지).
  const narrowViewport = useIsMobile(BOARD_STACK_QUERY);
  // 보드 상자(본문)의 실제 폭. 0 = 못 잰 상태(jsdom 등) → 뷰포트 판정만 쓴다.
  const [boardWidth, setBoardWidth] = useState(0);
  const boardObserverRef = useRef(null);
  const schedulesRequestRef = useRef(0);
  // '전체 해제'로 사용자가 직접 비운 선택은 자동 첫 프로젝트 선택으로 되살리지 않는다.
  const selectionTouchedRef = useRef(false);
  // 길게 눌러 이름을 펼친 카드 id(하나만). fired = 길게 누르기가 이미 동작했다 → 뒤따르는 click 은 수정 팝업을 열지 않는다.
  const [expandedNameId, setExpandedNameId] = useState(null);
  const longPressRef = useRef({ timer: null, fired: false, x: null, y: null });

  const loadProjects = useCallback(async (preferredId = null) => {
    setLoading(true);
    setPageError('');
    try {
      const response = await supportBoardAPI.list();
      const nextProjects = Array.isArray(response.data?.projects) ? response.data.projects : [];
      setProjects(nextProjects);
      setSelectedProjectIds((current) => {
        // 사라진 프로젝트는 선택에서 빼고, 새로 만든 프로젝트(preferredId)만 기존 선택에 더한다.
        const alive = orderSelection(current, nextProjects);
        const canPrefer = preferredId !== null && preferredId !== undefined
          && nextProjects.some((project) => project.id === preferredId);
        const next = canPrefer && !alive.includes(preferredId)
          ? orderSelection([...alive, preferredId], nextProjects)
          : alive;
        return sameIdList(next, current) ? current : next;
      });
    } catch (error) {
      setPageError(apiErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, []);

  // 배치 조회 API 가 없어 프로젝트별로 받아 합친다.
  // 🔴 세대 카운터 유지 필수 — 늦게 온 옛 응답이 최신 선택을 덮으면 해제한 프로젝트 일정이 되살아난다.
  const loadSchedulesFor = useCallback(async (projectIds) => {
    const ids = Array.isArray(projectIds) ? projectIds : [];
    const requestId = schedulesRequestRef.current + 1;
    schedulesRequestRef.current = requestId;
    if (!ids.length) {
      setSchedulesByProject({});
      setSchedulesLoading(false);
      return;
    }

    // 이미 받아 둔 막대는 지우지 않는다 — 하나 더 고를 때마다 달력이 깜빡이면 안 된다.
    setSchedulesLoading(true);
    const results = await mapWithLimit(ids, SCHEDULE_FETCH_LIMIT, async (projectId) => {
      try {
        const response = await supportBoardAPI.listSchedules(projectId);
        const rows = Array.isArray(response.data?.schedules) ? response.data.schedules : [];
        // project_id 는 서버가 이미 주지만(없으면 칩·색이 떨어진다) 방어적으로 채운다.
        return {
          projectId,
          rows: rows.map((row) => (row.project_id == null ? { ...row, project_id: projectId } : row)),
        };
      } catch (error) {
        return { projectId, error };
      }
    });
    if (schedulesRequestRef.current !== requestId) return;

    // 한 건이 실패해도(레이트리밋 등) 성공분은 살린다 — 실패한 프로젝트만 직전 값을 유지한다.
    const failed = results.find((result) => result.error);
    setSchedulesByProject((previous) => {
      const nextMap = {};
      results.forEach((result) => {
        nextMap[result.projectId] = result.error
          ? (previous[result.projectId] || [])
          : result.rows;
      });
      return nextMap;
    });
    if (failed) setPageError(apiErrorMessage(failed.error));
    setSchedulesLoading(false);
  }, []);

  const loadAssignees = useCallback(async () => {
    try {
      const response = await supportBoardAPI.assignees();
      setAssignees(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setPageError(apiErrorMessage(error));
    }
  }, []);

  useEffect(() => {
    loadProjects();
    loadAssignees();
  }, [loadAssignees, loadProjects]);

  // 기본 화면 = 완료가 아닌 것, 보관함 = 완료된 것. 목록을 다시 받지 않고 파생 목록만 갈아 끼운다.
  // 🔴 useMemo 로 참조를 고정한다 — 매 렌더 새 배열이면 아래 effect 가 매번 돌아 setState 루프가 된다.
  const visibleProjects = useMemo(
    () => projects.filter((project) => (
      archiveView ? isArchivedProject(project) : !isArchivedProject(project)
    )),
    [archiveView, projects],
  );

  const personById = useMemo(() => new Map(assignees.map((person) => [person.id, person])), [assignees]);

  const colorMap = useMemo(() => buildProjectColorMap(visibleProjects), [visibleProjects]);
  const projectNames = useMemo(() => {
    const names = {};
    visibleProjects.forEach((project) => { names[project.id] = project.name; });
    return names;
  }, [visibleProjects]);

  // 지금 뷰에 없는 프로젝트가 선택된 채로 남으면 캘린더·일정이 화면에 없는 프로젝트 것을 계속 그린다.
  // 선택이 비면 첫 프로젝트를 자동 선택하되(기존 동작), 사용자가 '전체 해제'로 비운 선택은 그대로 둔다.
  useEffect(() => {
    setSelectedProjectIds((current) => {
      const alive = orderSelection(current, visibleProjects);
      if (!alive.length && !selectionTouchedRef.current && visibleProjects.length > 0) {
        return [visibleProjects[0].id];
      }
      return sameIdList(alive, current) ? current : alive;
    });
  }, [visibleProjects]);

  // 🔴 배열을 의존성에 그대로 넣으면 매 렌더 새 참조라 무한 재조회가 된다 → 문자열 키로 고정한다.
  const selectedIdKey = selectedProjectIds.join(',');

  useEffect(() => {
    loadSchedulesFor(selectedIdKey ? selectedIdKey.split(',').map(Number) : []);
  }, [loadSchedulesFor, selectedIdKey]);

  const multiSelect = selectedProjectIds.length > 1;
  const canAddSchedule = selectedProjectIds.length === 1;

  // 선택된 프로젝트 일정을 한 목록으로 합친다. 단일 선택이면 서버 순서 그대로(= 기존 화면 불변).
  const schedules = useMemo(() => {
    const ids = selectedIdKey ? selectedIdKey.split(',').map(Number) : [];
    const merged = ids.reduce((rows, id) => rows.concat(schedulesByProject[id] || []), []);
    if (ids.length <= 1) return merged;
    return merged.sort((a, b) => (
      String(a.start_date).localeCompare(String(b.start_date))
      || String(a.project_id).localeCompare(String(b.project_id))
      || String(a.id).localeCompare(String(b.id))
    ));
  }, [schedulesByProject, selectedIdKey]);

  const toggleProject = (projectId) => {
    selectionTouchedRef.current = true;
    setSelectedProjectIds((current) => (
      current.includes(projectId)
        ? current.filter((id) => id !== projectId)
        : orderSelection([...current, projectId], visibleProjects)
    ));
  };

  const selectAllProjects = () => {
    selectionTouchedRef.current = true;
    setSelectedProjectIds(visibleProjects.map((project) => project.id));
  };

  const clearProjectSelection = () => {
    selectionTouchedRef.current = true;
    setSelectedProjectIds([]);
  };

  const rowAccent = (projectId) => (multiSelect
    ? { borderLeft: `3px solid ${projectColorOf(colorMap, projectId).bar}` }
    : null);

  // 🔴 badge 의 nowrap 을 풀어 긴 프로젝트명은 칩 안에서 줄바꿈한다 — nowrap 칩은 줄어들지 못해 좁은 일정 패널
  //    (캘린더 옆 최소 285)의 내용 최소 폭을 밀어 올리고, 내용이 칸 밖으로 삐져나가 본문(main)이 가로로 넘친다.
  const chipStyle = (projectId) => {
    const color = projectColorOf(colorMap, projectId);
    return { ...badge(), maxWidth: '100%', whiteSpace: 'normal', wordBreak: 'break-word', background: color.bar, color: color.text };
  };

  const refreshSelection = async () => {
    if (!selectedProjectIds.length) return;
    await Promise.all([loadProjects(), loadSchedulesFor(selectedProjectIds)]);
  };

  const saveSchedule = async (event) => {
    event.preventDefault();
    const targetId = canAddSchedule ? selectedProjectIds[0] : null;
    if (!targetId) {
      setPageError(`${SINGLE_SELECT_HINT}.`);
      return;
    }
    const title = scheduleTitle.trim();
    if (!title || !scheduleStart || !scheduleEnd) {
      setPageError('일정 제목과 기간을 입력하세요.');
      return;
    }
    if (scheduleStart > scheduleEnd) {
      setPageError('종료일은 시작일보다 빠를 수 없습니다.');
      return;
    }

    setSavingSchedule(true);
    setPageError('');
    try {
      await supportBoardAPI.createSchedule(targetId, {
        kind: 'task',
        title,
        start_date: scheduleStart,
        end_date: scheduleEnd,
        assignee_user_ids: scheduleAssigneeIds,
        // 조각1(서버 복수 담당자)이 아직 안 올라간 환경에서도 대표 1명은 저장된다.
        assignee_user_id: scheduleAssigneeIds[0] ?? null,
      });
      setScheduleTitle('');
      setScheduleAssigneeIds([]);
      await refreshSelection();
    } catch (error) {
      setPageError(apiErrorMessage(error));
    } finally {
      setSavingSchedule(false);
    }
  };

  const toggleScheduleDone = async (schedule) => {
    const nextDone = !schedule.done;
    const message = nextDone ? '이 일정을 완료 처리할까요?' : '이 일정의 완료 처리를 취소할까요?';
    if (!(await confirmDialog(message))) return;
    try {
      await supportBoardAPI.updateSchedule(schedule.id, { done: nextDone });
      await refreshSelection();
    } catch (error) {
      alertDialog(`일정 변경 실패: ${apiErrorMessage(error)}`);
    }
  };

  const deleteSchedule = async (schedule) => {
    if (!(await confirmDialog(`'${schedule.title}' 일정을 삭제할까요?`, { danger: true }))) return;
    try {
      await supportBoardAPI.deleteSchedule(schedule.id);
      await refreshSelection();
    } catch (error) {
      alertDialog(`일정 삭제 실패: ${apiErrorMessage(error)}`);
    }
  };

  // 확인은 수정 팝업의 '삭제'가 받는다. 하위 일정(FK CASCADE)과 담당자 캘린더 반영은 서버 DELETE 가 함께 한다.
  const deleteProject = async (project) => {
    try {
      await supportBoardAPI.remove(project.id);
      await loadProjects();
      return true;
    } catch (error) {
      alertDialog(`프로젝트 삭제 실패: ${apiErrorMessage(error)}`);
      return false;
    }
  };

  const moveMonth = (amount) => {
    setViewDate((current) => new Date(current.getFullYear(), current.getMonth() + amount, 1, 12));
  };

  // 목록 표시 = 지정된 담당자 전원(쉼표). 1명이면 종전 문자열과 완전히 같고, 0명이면 '담당자 없음'.
  const assigneeLabel = (item) => {
    const names = assigneeNames(assigneeIdsOf(item), personById);
    return names.length ? names.join(', ') : '담당자 없음';
  };

  // 달력 막대 tooltip 용 — 담당자가 없으면 빈 문자열이라 막대 tooltip 이 종전과 같다.
  const assigneeTooltip = (item) => assigneeNames(assigneeIdsOf(item), personById).join(', ');

  // 언마운트 때 대기 중인 길게 누르기 타이머를 지운다. ref 객체는 바뀌지 않고 필드만 바뀐다.
  useEffect(() => {
    const press = longPressRef.current;
    return () => clearTimeout(press.timer);
  }, []);

  const cancelNamePress = () => {
    const press = longPressRef.current;
    clearTimeout(press.timer);
    press.timer = null;
  };

  const startNamePress = (event, projectId) => {
    const press = longPressRef.current;
    clearTimeout(press.timer);
    const touch = event.touches?.[0];
    press.fired = false;
    press.x = touch ? touch.clientX : null;
    press.y = touch ? touch.clientY : null;
    press.timer = setTimeout(() => {
      press.timer = null;
      press.fired = true;
      setExpandedNameId((current) => (current === projectId ? null : projectId));
    }, NAME_LONG_PRESS_MS);
  };

  // 세로 스크롤 중에는 펼치지 않는다 — 좌표를 못 읽어도 취소한다.
  const moveNamePress = (event) => {
    const press = longPressRef.current;
    if (!press.timer) return;
    const touch = event.touches?.[0];
    if (!touch || press.x === null || press.y === null
      || Math.abs(touch.clientX - press.x) > NAME_LONG_PRESS_MOVE_PX
      || Math.abs(touch.clientY - press.y) > NAME_LONG_PRESS_MOVE_PX) {
      cancelNamePress();
    }
  };

  const endNamePress = (event) => {
    cancelNamePress();
    if (longPressRef.current.fired) event.preventDefault();
  };

  const blockNamePressMenu = (event) => {
    const press = longPressRef.current;
    if (press.timer || press.fired) event.preventDefault();
  };

  const openProjectCard = (project) => {
    const press = longPressRef.current;
    if (press.fired) {
      press.fired = false;
      return;
    }
    setProjectModal({ open: true, project });
  };

  // 보드 상자 ref — 붙는 즉시 폭을 재고, ResizeObserver 로 그 뒤 변화(창 크기·사이드바 폭/접힘)도 따라간다.
  // 🔴 콜백 ref 로 둔다 — effect 로 재면 첫 프레임이 넘치는 3단으로 한 번 그려진다.
  const setBoardNode = useCallback((node) => {
    if (boardObserverRef.current) {
      boardObserverRef.current.disconnect();
      boardObserverRef.current = null;
    }
    if (!node) return;
    const measure = () => {
      const width = node.getBoundingClientRect?.().width;
      // 내림 — 1290.6 을 1291 로 올려 3단을 고르면 0.4px 넘친다.
      setBoardWidth(Number.isFinite(width) && width > 0 ? Math.floor(width) : 0);
    };
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(measure);
      observer.observe(node);
      boardObserverRef.current = observer;
    }
    measure();
  }, []);

  const boardMeasured = boardWidth > 0;
  const stacked = narrowViewport || (boardMeasured && boardWidth < THREE_COLUMN_MIN_WIDTH);
  // stacked 아래 칸에서 캘린더가 최소 폭을 못 받으면 일정 패널을 옆이 아니라 아래에 둔다.
  const calendarBesidePanel = !boardMeasured || boardWidth >= CALENDAR_PANEL_MIN_WIDTH;
  // 프로젝트가 1개 이상일 때만 3단으로 편다 — 0개·첫 로딩 중에는 종전처럼 목록 안내만 전폭으로 보인다.
  const threeColumn = !stacked && visibleProjects.length > 0;

  return (
    <AppShell app="supportboard" style={{ fontFamily: 'var(--gw-font)', background: COLORS.bg }}>
      <main style={{ flex: 1, minWidth: 0, overflowY: 'auto', padding: '28px' }}>
        <div style={{ maxWidth: '1520px', margin: '0 auto' }}>
          <PageHeader
            icon={IconLayoutKanban}
            tone="green"
            title="경영지원팀 보드"
            subtitle={archiveView ? '완료된 프로젝트를 모아 보관합니다.' : '프로젝트 목표와 일정을 한눈에 관리합니다.'}
            actions={(
              <>
                <Button
                  variant={archiveView ? 'primary' : 'secondary'}
                  aria-pressed={archiveView}
                  onClick={() => { selectionTouchedRef.current = false; setArchiveView((current) => !current); }}
                >
                  보관함
                </Button>
                <Button variant="primary" onClick={() => setProjectModal({ open: true, project: null })}>
                  + 프로젝트
                </Button>
              </>
            )}
          />

          {pageError && (
            <div role="alert" style={{ marginBottom: '14px', background: TONES.red.soft, color: TONES.red.ink, borderRadius: RADIUS.lg, padding: '12px 14px', fontSize: '13px' }}>
              {pageError}
            </div>
          )}

          <div
            ref={setBoardNode}
            data-board-layout={stacked ? 'stacked' : 'three-column'}
            style={threeColumn
              ? { display: 'grid', gridTemplateColumns: THREE_COLUMN_TEMPLATE, gap: `${BOARD_GAP}px`, alignItems: 'start' }
              : undefined}
          >
            <section aria-label="프로젝트 목록" style={threeColumn ? { minWidth: 0 } : { marginBottom: '18px' }}>
              {/* 선택 그룹은 스크롤 영역 밖(목록 위)에 고정한다. */}
              {visibleProjects.length > 0 && (
                <div role="group" aria-label="프로젝트 선택" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
                  <button type="button" onClick={selectAllProjects} style={btn('secondary', 'sm')}>전체 선택</button>
                  <button type="button" onClick={clearProjectSelection} style={btn('secondary', 'sm')}>전체 해제</button>
                  <span style={{ color: COLORS.textSub, fontSize: '12px' }}>
                    선택 {selectedProjectIds.length}/{visibleProjects.length}
                  </span>
                </div>
              )}
              {/* 재조회(일정 추가·완료 토글 뒤 refreshSelection) 때 카드를 로딩 문구로 바꾸면 목록 스크롤이 맨 위로 튄다
                  → 로딩 문구는 보여 줄 카드가 하나도 없을 때만 띄운다. */}
              {loading && visibleProjects.length === 0 ? (
                <div role="status" style={{ textAlign: 'center', padding: '36px 16px', fontSize: '12px', color: `var(--gw-muted, ${COLORS.textMuted})` }}>프로젝트를 불러오는 중…</div>
              ) : visibleProjects.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '36px 16px', fontSize: '12px', color: `var(--gw-muted, ${COLORS.textMuted})` }}>
                  {archiveView
                    ? '완료된 프로젝트가 없습니다.'
                    : '등록된 프로젝트가 없습니다. 첫 프로젝트를 추가해 보세요.'}
                </div>
              ) : (
                <div
                  data-testid="support-board-project-scroll"
                  style={{
                    display: 'grid',
                    gap: '10px',
                    alignContent: 'start',
                    gridTemplateColumns: stacked ? PROJECT_LIST_COLUMNS_STACKED : PROJECT_LIST_COLUMNS,
                    // 목록만 따로 스크롤한다 — 끝까지 내려도 페이지 스크롤로 번지지 않게 막는다.
                    // 한 축만 auto 면 다른 축도 auto 로 계산된다 → 가로는 명시적으로 hidden.
                    overflowX: 'hidden',
                    overflowY: 'auto',
                    overscrollBehavior: 'contain',
                    maxHeight: `${stacked ? PROJECT_LIST_MAX_HEIGHT_STACKED : PROJECT_LIST_MAX_HEIGHT}px`,
                    // 선택 링(2px)이 스크롤 영역 가장자리에서 잘리지 않게 안쪽 여백을 둔다.
                    padding: '3px 6px 3px 3px',
                  }}
                >
                  {visibleProjects.map((project) => {
                    const selected = selectedProjectIds.includes(project.id);
                    const projectColor = projectColorOf(colorMap, project.id);
                    const progressWidth = projectProgressWidth(project.progress);
                    const edgeColor = selected ? COLORS.primary : COLORS.border;
                    const nameExpanded = expandedNameId === project.id;
                    return (
                      <article
                        key={project.id}
                        data-selected={selected ? 'true' : 'false'}
                        style={{
                          display: 'flex',
                          alignItems: 'stretch',
                          minWidth: 0,
                          background: card.background,
                          borderRadius: card.borderRadius,
                          // 🔴 테두리는 네 변을 한 번에 쓴다 — border/borderLeft 단축형과 borderColor 를 섞으면
                          //    선택이 바뀔 때 React 가 borderColor 만 다시 써서 왼쪽 색 띠가 테두리 색으로 덮인다.
                          borderStyle: 'solid',
                          borderWidth: '1px 1px 1px 4px',
                          borderColor: `${edgeColor} ${edgeColor} ${edgeColor} ${projectColor.bar}`,
                          boxShadow: selected ? `0 0 0 2px ${COLORS.primary}` : card.boxShadow,
                        }}
                      >
                        {/* 🔴 체크박스와 열기 버튼은 형제로 둔다(버튼 안에 input 금지, article 에 onClick 금지)
                            — 체크박스는 선택만 바꾸고, 카드 나머지는 수정 팝업만 연다. */}
                        <label style={{ display: 'flex', alignItems: 'flex-start', padding: '14px 6px 14px 10px', cursor: 'pointer', flexShrink: 0 }}>
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => toggleProject(project.id)}
                            aria-label={`${project.name} 선택`}
                            style={{ width: '16px', height: '16px', margin: 0, cursor: 'pointer', accentColor: COLORS.primary }}
                          />
                        </label>
                        {/* 터치 길게 누르기 = 이름 펼치기/접기(수정 팝업 안 열림), 짧은 탭·마우스 클릭 = 수정 팝업.
                            마우스는 이름의 title 툴팁(가장 안쪽 title 이 이긴다)으로 전체 이름을 본다. */}
                        <button
                          type="button"
                          onClick={() => openProjectCard(project)}
                          onTouchStart={(event) => startNamePress(event, project.id)}
                          onTouchMove={moveNamePress}
                          onTouchEnd={endNamePress}
                          onTouchCancel={cancelNamePress}
                          onContextMenu={blockNamePressMenu}
                          aria-label={`${project.name} 수정`}
                          title="프로젝트 수정"
                          style={{ flex: 1, minWidth: 0, padding: '12px 12px 12px 4px', border: 0, background: 'transparent', textAlign: 'left', cursor: 'pointer', WebkitTouchCallout: 'none' }}
                        >
                          {/* 첫 줄 = [색 점 · 이름]. 이름은 최대 2줄(넘치면 …), 펼치면 전체. 🔴 이름 글자는 이 strong 하나에만 둔다
                              — 시험이 getByText(이름).closest('article') 로 카드를 찾는다. */}
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '7px', minWidth: 0 }}>
                            {/* 카드 색 점 = 달력 막대 색 — 다중 선택 때 어느 막대가 어느 프로젝트인지 눈으로 잇는다. */}
                            <span aria-hidden style={{ width: '8px', height: '8px', marginTop: '6px', flexShrink: 0, borderRadius: '50%', background: projectColor.bar }} />
                            <strong
                              data-part="name"
                              data-expanded={nameExpanded ? 'true' : 'false'}
                              title={project.name}
                              style={{
                                flex: 1,
                                minWidth: 0,
                                color: COLORS.text,
                                fontSize: '15px',
                                lineHeight: 1.35,
                                whiteSpace: 'normal',
                                wordBreak: 'keep-all',
                                overflowWrap: 'anywhere',
                                ...(nameExpanded
                                  ? null
                                  : { display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'hidden' }),
                              }}
                            >
                              {project.name}
                            </strong>
                          </div>
                          {/* 둘째 줄 = [진행률 · 바 · % · 상태 배지]. 가장 좁은 3단 카드(안쪽 약 178)에서도 바에 약 45 가 남는다.
                              flexWrap 은 극단적으로 좁을 때만 배지를 다음 줄 오른쪽으로 내리는 안전판이다.
                              🔴 '진행률' 라벨과 '%' 를 지우지 말 것 — e2e 가 '진행률 N%' 패턴으로 이 줄을 읽는다
                              (둘 사이에는 글자 없는 바만 둔다). */}
                          <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px', minWidth: 0, color: COLORS.textSub, fontSize: '12px' }}>
                            <span style={{ flexShrink: 0 }}>진행률</span>
                            <div data-part="progress-track" style={{ flex: '1 1 0', minWidth: '28px', height: '7px', overflow: 'hidden', borderRadius: RADIUS.pill, background: COLORS.borderSoft }}>
                              <div data-part="progress-fill" style={{ width: progressWidth, height: '100%', borderRadius: RADIUS.pill, background: COLORS.primary }} />
                            </div>
                            <span data-part="progress-text" style={{ flexShrink: 0, minWidth: '30px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{progressWidth}</span>
                            <span data-part="status" style={{ ...badge(statusTone(project.status)), padding: '2px 8px', flexShrink: 0, marginLeft: 'auto' }}>
                              {STATUS_LABELS[project.status] || project.status}
                            </span>
                          </div>
                        </button>
                      </article>
                    );
                  })}
                </div>
              )}
            </section>

            {visibleProjects.length > 0 && (
              // 3단에서는 이 래퍼를 display:contents 로 풀어 캘린더·일정 패널이 바깥 grid 의 2·3열이 된다.
              // 🔴 두 배치의 노드 구조를 같게 둔다 — 갈아 끼우면 목록 section 이 다시 마운트된다.
              // stacked 의 아래 칸도 캘린더 최소 폭(734)을 지킨다 — 모자라면 일정 패널을 캘린더 아래로 내린다.
              <div
                style={threeColumn
                  ? { display: 'contents' }
                  : {
                    display: 'grid',
                    gridTemplateColumns: calendarBesidePanel ? CALENDAR_PANEL_TEMPLATE : 'minmax(0, 1fr)',
                    gap: `${BOARD_GAP}px`,
                    alignItems: 'start',
                  }}
              >
                <div style={{ minWidth: 0, overflowX: 'auto' }}>
                  {schedulesLoading && schedules.length === 0 ? (
                    <div style={{ ...card, color: COLORS.textSub }}>일정을 불러오는 중…</div>
                  ) : (
                    <CalendarGrid
                      year={viewDate.getFullYear()}
                      month={viewDate.getMonth()}
                      schedules={schedules}
                      projectColors={multiSelect ? colorMap : null}
                      projectNames={multiSelect ? projectNames : null}
                      assigneeLabelOf={assigneeTooltip}
                      onPreviousMonth={() => moveMonth(-1)}
                      onNextMonth={() => moveMonth(1)}
                    />
                  )}
                </div>

                <aside style={{ display: 'grid', gap: '14px' }}>
                  <form onSubmit={saveSchedule} style={card}>
                    <h3 style={{ margin: '0 0 14px', color: COLORS.text, fontSize: '16px' }}>일정 추가</h3>
                    {!canAddSchedule && (
                      <div style={{ margin: '0 0 12px', padding: '8px 10px', borderRadius: RADIUS.md, background: COLORS.warnSoft, color: COLORS.warn, fontSize: '12px', fontWeight: 700 }}>
                        {SINGLE_SELECT_HINT}
                      </div>
                    )}
                    <label style={{ display: 'block', marginBottom: '11px', color: COLORS.text, fontSize: '12px', fontWeight: 700 }}>
                      제목
                      <input
                        value={scheduleTitle}
                        onChange={(event) => setScheduleTitle(event.target.value)}
                        maxLength={200}
                        required
                        disabled={!canAddSchedule}
                        style={{ ...inputStyle, marginTop: '5px' }}
                      />
                    </label>
                    {/* 🔴 '1fr 1fr' 로 되돌리지 말 것 — 1fr 의 최소값은 auto(내용 최소 폭)라 date input 고유 최소 폭이
                        두 칸을 밀어 올린다 → 캘린더 옆 좁은 일정 패널(1366 폭 ≈295)에서 폼이 칸 밖으로 삐져나가 main 이 넘친다. */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px', marginBottom: '11px' }}>
                      <label style={{ color: COLORS.text, fontSize: '12px', fontWeight: 700 }}>
                        시작일
                        <input type="date" value={scheduleStart} onChange={(event) => setScheduleStart(event.target.value)} required disabled={!canAddSchedule} style={{ ...inputStyle, minWidth: 0, marginTop: '5px', padding: '8px 7px' }} />
                      </label>
                      <label style={{ color: COLORS.text, fontSize: '12px', fontWeight: 700 }}>
                        종료일
                        <input type="date" value={scheduleEnd} onChange={(event) => setScheduleEnd(event.target.value)} required disabled={!canAddSchedule} style={{ ...inputStyle, minWidth: 0, marginTop: '5px', padding: '8px 7px' }} />
                      </label>
                    </div>
                    {/* 🔴 라벨은 div 로 둔다 — label 이 role=group 을 감싸면 접근성 이름이 어긋난다. */}
                    <div style={{ marginBottom: '14px' }}>
                      <div style={{ marginBottom: '5px', color: COLORS.text, fontSize: '12px', fontWeight: 700 }}>담당자</div>
                      <AssigneeTags
                        ids={scheduleAssigneeIds}
                        people={assignees}
                        onChange={setScheduleAssigneeIds}
                        groupLabel="일정 담당자"
                        disabled={!canAddSchedule}
                      />
                    </div>
                    <button type="submit" disabled={savingSchedule || !canAddSchedule} style={{ ...btn('primary'), width: '100%', opacity: (savingSchedule || !canAddSchedule) ? 0.65 : 1 }}>
                      {savingSchedule ? '추가 중…' : '일정 추가'}
                    </button>
                  </form>

                  <section aria-label="일정 관리" style={card}>
                    <h3 style={{ margin: '0 0 12px', color: COLORS.text, fontSize: '16px' }}>일정 관리</h3>
                    {schedules.length === 0 ? (
                      <div style={{ color: COLORS.textMuted, fontSize: '12px' }}>등록된 일정이 없습니다.</div>
                    ) : (
                      <div style={{ display: 'grid', gap: '8px' }}>
                        {schedules.map((schedule) => (
                          <div key={schedule.id} style={{ padding: '9px', border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.md, ...rowAccent(schedule.project_id) }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '7px' }}>
                              {schedule.kind === 'task' ? (
                                <input
                                  type="checkbox"
                                  checked={Boolean(schedule.done)}
                                  onChange={() => toggleScheduleDone(schedule)}
                                  aria-label={`${schedule.title} 완료`}
                                  style={{ marginTop: '2px' }}
                                />
                              ) : (
                                <span aria-label="최종 목표" style={{ color: COLORS.primary }}>◆</span>
                              )}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                {multiSelect && (
                                  <div style={{ marginBottom: '4px' }}>
                                    <span data-project-chip={schedule.project_id} style={chipStyle(schedule.project_id)}>
                                      {projectNames[schedule.project_id] || '기타 프로젝트'}
                                    </span>
                                  </div>
                                )}
                                <div style={{ color: COLORS.text, fontSize: '12px', fontWeight: 700, textDecoration: schedule.done ? 'line-through' : 'none', wordBreak: 'break-word' }}>
                                  {schedule.title}
                                </div>
                                <div style={{ marginTop: '3px', color: COLORS.textMuted, fontSize: '11px' }}>
                                  {formatDate(schedule.start_date)} ~ {formatDate(schedule.end_date)} · {assigneeLabel(schedule)}
                                </div>
                              </div>
                              <button type="button" onClick={() => setScheduleModal({ open: true, schedule })} aria-label={`${schedule.title} 수정`} style={{ ...btn('secondary', 'sm'), padding: '3px 7px' }}>✏</button>
                              <button type="button" onClick={() => deleteSchedule(schedule)} aria-label={`${schedule.title} 삭제`} style={{ ...btn('danger', 'sm'), padding: '3px 7px' }}>×</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                </aside>
              </div>
            )}
          </div>
        </div>
      </main>

      {projectModal.open && (
        <ProjectModal
          key={projectModal.project?.id || 'new'}
          project={projectModal.project}
          statusLabels={STATUS_LABELS}
          assignees={assignees}
          onClose={() => setProjectModal({ open: false, project: null })}
          onSaved={async (savedProject) => {
            const creating = !projectModal.project;
            setProjectModal({ open: false, project: null });
            // 새로 만든 프로젝트가 지금 뷰에 안 들어오면 화면에서 사라진 것처럼 보인다 → 그 뷰로 따라간다.
            // (수정은 따라가지 않는다 — 완료 처리한 카드가 기본 화면에서 빠지는 게 보관함의 목적이다.)
            if (creating) setArchiveView(isArchivedProject(savedProject));
            // 🔴 새로 만든 프로젝트만 선택에 더한다 — 수정까지 더하면 카드 하나를 고쳤을 뿐인데
            //    선택이 2개로 늘어 '일정 추가' 폼이 갑자기 잠긴다.
            await loadProjects(creating ? (savedProject?.id ?? null) : null);
            // 선택 키가 안 바뀌면 재조회 effect 가 안 돈다 → 고친 프로젝트가 선택 중이면 직접 다시 읽는다.
            if (savedProject?.id && selectedProjectIds.includes(savedProject.id)) {
              await loadSchedulesFor(selectedProjectIds);
            }
          }}
          // 실패하면(alert) 팝업을 열어 둔다. 삭제 뒤 선택 정리·일정 재조회는 loadProjects 와 선택 effect 가 한다.
          onDelete={async (target) => {
            if (await deleteProject(target)) setProjectModal({ open: false, project: null });
          }}
        />
      )}

      {scheduleModal.open && (
        <ScheduleModal
          key={scheduleModal.schedule?.id || 'schedule'}
          schedule={scheduleModal.schedule}
          assignees={assignees}
          onClose={() => setScheduleModal({ open: false, schedule: null })}
          onSaved={async () => {
            setScheduleModal({ open: false, schedule: null });
            await refreshSelection();
          }}
        />
      )}
    </AppShell>
  );
}
